import { useCallback, useEffect, useMemo, useState } from 'react';
import { dispatchTimelineRowCreated } from '../../modal_host/events';

import {
  createDayWorkflow,
  fetchWorkflowDetail,
  fetchWorkflows,
} from '../lib/api';
import { getLocalDateString } from '../lib/time';
import { buildDayWorkflowPlan } from '../../../common/workflowTiming';

function dispatchToast({
  variant = 'secondary',
  title = 'Timeline',
  message = '',
  delay = 5000,
}) {
  document.dispatchEvent(new CustomEvent('ui:toast', {
    detail: {
      variant,
      title,
      message,
      delay,
    },
  }));
}

function dispatchTimelineChanged() {
  document.dispatchEvent(new CustomEvent('timeline:changed'));
}

export default function useTimelineSidebar(selectedDate) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 768px)').matches;
  });

  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowDetailLoading, setWorkflowDetailLoading] = useState(false);
  const [workflowDetailReloadVersion, setWorkflowDetailReloadVersion] = useState(0);

  const [startDate, setStartDate] = useState(selectedDate || getLocalDateString());
  const [timeMode, setTimeMode] = useState('start');
  const [timeValue, setTimeValue] = useState('09:00');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setStartDate(selectedDate || getLocalDateString());
  }, [selectedDate]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const media = window.matchMedia('(max-width: 768px)');

    function apply(matches) {
      setCollapsed(matches);
    }

    apply(media.matches);

    function onChange(event) {
      apply(Boolean(event?.matches));
    }

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const loadWorkflowOptions = useCallback(async ({ preferredWorkflow = null } = {}) => {
    setWorkflowsLoading(true);
    try {
      const data = await fetchWorkflows();
      const rows = Array.isArray(data) ? data : [];
      const normalizedPreferredId = preferredWorkflow?.id ? String(preferredWorkflow.id) : '';
      const preferredName = String(preferredWorkflow?.name ?? '').trim();

      const nextOptions = normalizedPreferredId && preferredName
        ? (() => {
          const alreadyIncluded = rows.some((workflow) => String(workflow?.id ?? '') === normalizedPreferredId);
          return alreadyIncluded
            ? rows
            : [{ id: Number(normalizedPreferredId), name: preferredName }, ...rows];
        })()
        : rows;

      setWorkflowOptions(nextOptions);

      if (normalizedPreferredId) {
        setSelectedWorkflowId(normalizedPreferredId);
      }
    } catch (error) {
      dispatchToast({
        variant: 'danger',
        title: 'Timeline',
        message: `Workflows konnten nicht geladen werden: ${error?.message ?? String(error)}`,
        delay: 7000,
      });
      setWorkflowOptions([]);
    } finally {
      setWorkflowsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkflowOptions();
  }, [loadWorkflowOptions]);

  useEffect(() => {
    function onCrudReload(event) {
      const entity = String(event?.detail?.entity ?? '').trim().toLowerCase().replace(/_/g, '-');
      if (entity !== 'workflows' && entity !== 'tasks') {
        return;
      }

      if (entity === 'workflows') {
        void loadWorkflowOptions();
      }
      setWorkflowDetailReloadVersion((current) => current + 1);
    }

    function onCrudCreated(event) {
      const entity = String(event?.detail?.entity ?? '').trim().toLowerCase().replace(/_/g, '-');
      if (entity !== 'workflows') {
        return;
      }

      const record = event?.detail?.record;
      const source = String(event?.detail?.source ?? '');
      const createdId = record?.id;
      const createdName = String(record?.name ?? '').trim();

      if (source !== 'timeline-sidebar') {
        void loadWorkflowOptions();
        return;
      }

      if (!createdId) {
        void loadWorkflowOptions();
        return;
      }

      setWorkflowOptions((current) => {
        const existing = Array.isArray(current) ? current : [];
        const filtered = existing.filter((workflow) => String(workflow?.id ?? '') !== String(createdId));
        return [{ id: createdId, name: createdName || `Workflow #${createdId}` }, ...filtered];
      });
      setSelectedWorkflowId(String(createdId));
      void loadWorkflowOptions({
        preferredWorkflow: {
          id: createdId,
          name: createdName,
        },
      });
    }

    document.addEventListener('crud:reload', onCrudReload);
    document.addEventListener('crud:created', onCrudCreated);
    return () => {
      document.removeEventListener('crud:reload', onCrudReload);
      document.removeEventListener('crud:created', onCrudCreated);
    };
  }, [loadWorkflowOptions]);

  useEffect(() => {
    if (!selectedWorkflowId) {
      setSelectedWorkflow(null);
      return undefined;
    }

    let active = true;
    setWorkflowDetailLoading(true);
    setSelectedWorkflow(null);

    void fetchWorkflowDetail(selectedWorkflowId)
      .then((data) => {
        if (!active) {
          return;
        }
        setSelectedWorkflow(data ?? null);
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        setSelectedWorkflow(null);
        dispatchToast({
          variant: 'danger',
          title: 'Timeline',
          message: `Workflow-Details konnten nicht geladen werden: ${error?.message ?? String(error)}`,
          delay: 7000,
        });
      })
      .finally(() => {
        if (active) {
          setWorkflowDetailLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedWorkflowId, workflowDetailReloadVersion]);

  const workflowTasks = useMemo(
    () => (Array.isArray(selectedWorkflow?.tasks) ? selectedWorkflow.tasks : []),
    [selectedWorkflow],
  );

  const dayWorkflowPlanning = useMemo(
    () => buildDayWorkflowPlan({
      workflowTasks,
      timeMode,
      timeValue,
    }),
    [workflowTasks, timeMode, timeValue],
  );

  const setTimeModePreservingPlan = useCallback((nextMode) => {
    const normalized = String(nextMode ?? 'start') === 'end' ? 'end' : 'start';
    setTimeMode((currentMode) => {
      if (currentMode === normalized) {
        return currentMode;
      }

      setTimeValue((currentValue) => {
        const currentPlan = buildDayWorkflowPlan({
          workflowTasks,
          timeMode: currentMode,
          timeValue: currentValue,
        });

        if (normalized === 'end') {
          return currentPlan.endTime ?? currentValue;
        }
        return currentPlan.startTime ?? currentValue;
      });

      return normalized;
    });
  }, [workflowTasks]);

  const applySelectedWorkflow = useCallback(async () => {
    if (!selectedWorkflowId) {
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message: 'Bitte zuerst einen Workflow auswählen.',
      });
      return;
    }

    if (!startDate) {
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message: 'Bitte ein Datum auswählen.',
      });
      return;
    }

    if (!timeValue) {
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message: timeMode === 'end' ? 'Bitte eine Endzeit auswählen.' : 'Bitte eine Startzeit auswählen.',
      });
      return;
    }

    if (!dayWorkflowPlanning.isValid) {
      const message = timeMode === 'end'
        ? 'Bitte eine gültige Endzeit eingeben.'
        : 'Bitte eine gültige Startzeit eingeben.';
      dispatchToast({
        variant: 'warning',
        title: 'Timeline',
        message,
      });
      return;
    }

    setApplying(true);
    try {
      const created = await createDayWorkflow({
        workflow_id: Number(selectedWorkflowId),
        date: startDate,
        time_mode: timeMode,
        anchor_time: String(timeValue ?? ''),
      });

      dispatchTimelineRowCreated({
        entity: 'day-workflows',
        id: created?.id,
        date: created?.date ?? startDate,
      });

      dispatchToast({
        variant: 'success',
        title: 'Day-Workflow',
        message: 'In Timeline übernommen.',
      });

      dispatchTimelineChanged();
    } catch (error) {
      dispatchToast({
        variant: 'danger',
        title: 'Day-Workflow',
        message: `Übernahme fehlgeschlagen: ${error?.message ?? String(error)}`,
        delay: 8000,
      });
    } finally {
      setApplying(false);
    }
  }, [selectedWorkflowId, startDate, timeValue, timeMode, dayWorkflowPlanning]);

  return {
    collapsed,
    setCollapsed,

    workflowOptions,
    workflowsLoading,

    selectedWorkflowId,
    setSelectedWorkflowId,
    selectedWorkflow,
    workflowTasks,
    workflowDetailLoading,

    startDate,
    setStartDate,
    timeMode,
    setTimeMode: setTimeModePreservingPlan,
    timeValue,
    setTimeValue,
    dayWorkflowPlanning,

    applying,
    applySelectedWorkflow,
  };
}
